import { Anchor, Card, CardBody, CardHeader, CardTitle, Grid, SimpleGrid, Text } from "@govlaunch/web";
import Box from "@govlaunch/web/src/box/Box";
import React from "react";
import { IProductFieldsFragment } from "../fragments/__generated__/ProductFieldsFragment.generated";
import findInUnionByTypename from "../utils/findInUnionByTypename";
import { AppStore, DeploymentCardIcon, PlayStore } from "./icons";
import { FormattedMessage, useIntl } from "react-intl";

interface IProductDeploymentCardProps {
  product: IProductFieldsFragment;
}

export default function ProductDeploymentCard({ product }: IProductDeploymentCardProps) {
  const intl = useIntl();

  if (product.deployment.length === 0) {
    return null;
  }

  const webDeployment = findInUnionByTypename(product.deployment, "WebProductDeployment");
  const desktopDeployment = findInUnionByTypename(product.deployment, "DesktopProductDeployment");
  const appStoreDeployment = findInUnionByTypename(product.deployment, "AppStoreProductDeployment");
  const playStoreDeployment = findInUnionByTypename(product.deployment, "PlayStoreProductDeployment");

  if (!(webDeployment || desktopDeployment || appStoreDeployment || playStoreDeployment)) {
    return null;
  }

  return (
    <Card mt={4}>
      <CardHeader>
        <DeploymentCardIcon />
        <CardTitle>
          <FormattedMessage defaultMessage="Deployment" id="OyvHWp" />
        </CardTitle>
      </CardHeader>

      <CardBody>
        <SimpleGrid gridTemplateColumns="1fr 1fr" gridRowGap={4}>
          <Box>
            <Text fontWeight="semibold" fontSize="sm">
              <FormattedMessage defaultMessage="Delivery" id="drqP2L" />
            </Text>

            <Text fontSize="sm">
              {[
                desktopDeployment ? intl.formatMessage({ defaultMessage: 'Desktop', id: 'tHSb8p' }) : null,
                appStoreDeployment || playStoreDeployment ? intl.formatMessage({ defaultMessage: 'Mobile', id: 'GWtmtu' }) : null,
                webDeployment ? intl.formatMessage({ defaultMessage: 'Web', id: 'B6CB37' }) : null,
              ]
                .filter(Boolean)
                .join(" / ")}
            </Text>
          </Box>

          {webDeployment && (
            <>
              <Box>
                <Text fontWeight="semibold" fontSize="sm">
                  <FormattedMessage defaultMessage="Mobile Responsive" id="xVXe3M" />
                </Text>
                <Text fontSize="sm">
                  {webDeployment.mobileResponsive ? (
                    <FormattedMessage defaultMessage="Yes" id="a5msuh" />
                  ) : (
                    <FormattedMessage defaultMessage="No" id="oUWADl" />
                  )}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="semibold" fontSize="sm">
                  <FormattedMessage defaultMessage="Browser Support" id="VVpE+u" />
                </Text>
                <Text fontSize="sm">{webDeployment.supportedBrowsers.join(" / ")}</Text>
              </Box>
            </>
          )}

          {desktopDeployment && (
            <Box>
              <Text fontWeight="semibold" fontSize="sm">
                <FormattedMessage defaultMessage="Platforms" id="6GGPoo" />
              </Text>
              <Text fontSize="sm">{desktopDeployment.platforms.join(" / ")}</Text>
            </Box>
          )}
        </SimpleGrid>

        {(appStoreDeployment || playStoreDeployment) && (
          <Box mt={4}>
            <Text fontWeight="semibold" fontSize="sm">
              <FormattedMessage defaultMessage="Mobile" id="GWtmtu" />
            </Text>

            <Grid gridTemplateColumns="1fr 1fr" columnGap={4} mt={1}>
              {appStoreDeployment && (
                <div>
                  <Anchor href={appStoreDeployment.linkHref} fontSize="sm" external="govlaunch">
                    <AppStore />
                  </Anchor>
                </div>
              )}

              {playStoreDeployment && (
                <div>
                  <Anchor href={playStoreDeployment.linkHref} fontSize="sm" external="govlaunch">
                    <PlayStore />
                  </Anchor>
                </div>
              )}
            </Grid>
          </Box>
        )}
      </CardBody>
    </Card>
  );
}
